import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import type { BaseCourseApi, BaseLearningPathApi } from "../../models/api";
import { Status } from "../../models/gameplan";
import {
  useCourseModules,
  useCourseProgress,
  useLearningPathProgress,
} from "../../shared/hooks";
import { getActiveCourse } from "../../shared/slices/activeCourse";
import { currentGamePlanIndexAtom } from "./atoms";

export enum GamePlanItemType {
  COMMITMENT = "COMMITMENT",
  MODULE = "MODULE",
  QUICKWIN = "QUICKWIN",
  LESSON = "LESSON",
  WORKOUT = "WORKOUT",
  DISCOVER = "DISCOVER",
  COMPLETED = "COMPLETED",
}

export interface GamePlanItem {
  id?: number;
  order?: number;
}

export interface GamePlanWorkout extends GamePlanItem {
  question: string;
  correctAnswer: string;
  answerJustification: string;
  answers: string;
  selectedAnswer?: string;
}

export interface GamePlanSlide extends GamePlanItem {
  type: string;
  title: string;
  body: string;
  imagePath: string;
}

export interface GamePlanPath {
  index?: number;
  label?: string;
  name: string;
  title?: string;
  status?: Status;
  learningPath?: BaseLearningPathApi;
  course?: BaseCourseApi;
  module?: GamePlanPath & {
    item?: GamePlanItem;
  };
  type: GamePlanItemType;
  item?: GamePlanItem | GamePlanWorkout | GamePlanSlide[];
  children?: GamePlanPath[];
  parent?: GamePlanPath;
}

const flatGamePlans = (
  gamePlans: GamePlanPath[],
  parent: GamePlanPath = undefined
) => {
  let flattenItems: GamePlanPath[] = [];

  for (const gamePlan of gamePlans) {
    gamePlan.parent = parent;
    flattenItems.push(gamePlan);
    if (gamePlan.children) {
      flattenItems = flattenItems.concat(
        flatGamePlans(gamePlan.children, gamePlan)
      );
    }
  }
  return flattenItems;
};

const addExtraFlattenProperties = (gamePlans: GamePlanPath[]) => {
  let index = 0;
  const findParentModule = (gameplan) => {
    if (gameplan.parent?.type === GamePlanItemType.MODULE)
      return gameplan.parent;
    if (gameplan.parent) return findParentModule(gameplan.parent);
  };

  for (const gameplan of gamePlans) {
    gameplan.index = index++;
    gameplan.module = findParentModule(gameplan);
  }
};

function findLastIndex(
  gamePlans: GamePlanPath[],
  predicate: (gamePlan: GamePlanPath) => boolean
) {
  for (let i = gamePlans.length - 1; i >= 0; i--) {
    if (predicate(gamePlans[i])) return i;
  }
  return -1;
}

export const useGamePlanPath = (paramCourseId?: BaseCourseApi["id"]) => {
  const defaultCourseId = useSelector(getActiveCourse);
  const { courseId: urlCourseId } = useParams<{ courseId?: string }>();
  const [isLearningPathStarted, setIsLearningPathStarted] = useState(false);
  const [isLearningPathFinished, setIsLearningPathFinished] = useState(false);
  const courseId = paramCourseId || urlCourseId || defaultCourseId;

  const {
    data: learningPathProgress,
    isFetching: isFetchingLearningPathProgress,
  } = useLearningPathProgress();
  const { data: courseProgress, isFetching: isFetchingCourseProgress } =
    useCourseProgress(courseId);
  const { data: courseModules, isFetching: isFetchingCourseModules } =
    useCourseModules(courseId);
  const [currentGamePlanIndex, setCurrentGamePlanIndex] = useAtom(
    currentGamePlanIndexAtom
  );

  const isLoading =
    isFetchingCourseProgress ||
    isFetchingCourseModules ||
    isFetchingLearningPathProgress;

  useEffect(() => {
    if (isLoading) return;

    const currentCourseLearningPathProgress =
      learningPathProgress?.learningPaths?.find((learningPath) => {
        return learningPath.learningPathId === courseProgress.learningPath?.id;
      });

    const completedPercentage =
      currentCourseLearningPathProgress?.completedPercentage;

    if (currentCourseLearningPathProgress && completedPercentage > 1) {
      setIsLearningPathStarted(true);
    }

    if (currentCourseLearningPathProgress && completedPercentage >= 100) {
      setIsLearningPathFinished(true);
    }
  }, [isLoading, learningPathProgress, courseProgress]);

  const memo = useMemo(() => {
    if (
      courseProgress &&
      courseModules &&
      courseProgress.modules?.length &&
      !isLoading
    ) {
      const getGamePlanItems = (): GamePlanPath[] => {
        return [
          {
            name: "Goal Commitment",
            status: courseProgress?.commitmentStatus,
            type: GamePlanItemType.COMMITMENT,
          },
          ...courseProgress.modules.map((module, moduleIndex): GamePlanPath => {
            const lessonCompletedCount = module.lessons.filter(
              (lesson) => lesson.status === Status.COMPLETED
            ).length;

            let moduleStatus = Status.NEW;
            switch (lessonCompletedCount) {
              case module.lessons.length: {
                moduleStatus = Status.COMPLETED;
                break;
              }
              case 0: {
                moduleStatus = Status.NEW;
                break;
              }
              default: {
                moduleStatus = Status.IN_PROGRESS;
                break;
              }
            }

            return {
              type: GamePlanItemType.MODULE,
              label: `Module ${moduleIndex + 1}`,
              name: module.name,
              title: module.title,
              status: moduleStatus,
              learningPath: courseProgress.learningPath,
              course: {
                id: courseProgress.id,
                name: courseProgress.name,
              },
              item: {
                id: module.id,
                order: module.order,
              },
              children: module.lessons.map(
                (lesson, lessonIndex): GamePlanPath => {
                  const { workouts, slides } =
                    courseModules[moduleIndex].lessons[lessonIndex];
                  return {
                    type: GamePlanItemType.LESSON,
                    name: lesson.name,
                    title:
                      courseModules[moduleIndex]?.lessons[lessonIndex]
                        ?.slides[0]?.title,
                    status: lesson.status,
                    item: {
                      id: lesson.id,
                      order: lesson.order,
                    },
                    children: [
                      ...workouts.map(
                        (workout, workoutIndex) =>
                          ({
                            type: GamePlanItemType.WORKOUT,
                            name: `Workout ${
                              workouts.length > 1 ? workoutIndex + 1 : ""
                            }`,
                            status: lesson.workouts[workoutIndex]?.status,
                            item: {
                              id: workout?.id,
                              question: workout?.question,
                              correctAnswer: workout?.correctAnswer,
                              answerJustification: workout?.answerJustification,
                              answers: workout?.answers,
                              selectedAnswer:
                                lesson.workouts[workoutIndex]?.answer,
                            },
                          } as GamePlanPath)
                      ),
                      {
                        type: GamePlanItemType.DISCOVER,
                        name: "Discover",
                        status: lesson.status,
                        item: slides,
                      } as GamePlanPath,
                      {
                        type: GamePlanItemType.COMPLETED,
                        name: lesson.name,
                        label: "lesson",
                      } as GamePlanPath,
                    ].filter(
                      (c) =>
                        lesson.order !== 0 ||
                        c.type === GamePlanItemType.DISCOVER
                    ),
                  };
                }
              ),
            };
          }),
          {
            type: GamePlanItemType.QUICKWIN,
            name: "Quick Wins",
            status: courseProgress?.quickWinStatus,
          },
          {
            type: GamePlanItemType.COMPLETED,
            name: courseProgress?.name,
            label: "course",
          },
        ];
      };

      let memoGamePlans: GamePlanPath[];
      let memoFlattenGamePlans: GamePlanPath[];
      let memoIsOneModuleCompleted: boolean | undefined;
      let memoLastInProgressIndex: number | undefined;
      let memoFirstNewIndex: number | undefined;
      let memoQuickWinIndex: number | undefined;
      let memoIsGamePlanCompleted: boolean;

      if (courseProgress && courseModules) {
        memoGamePlans = getGamePlanItems();
        memoFlattenGamePlans = flatGamePlans(memoGamePlans).filter(
          (i) =>
            i.type !== GamePlanItemType.MODULE &&
            i.type !== GamePlanItemType.LESSON
        );
        addExtraFlattenProperties(memoFlattenGamePlans);

        memoLastInProgressIndex = findLastIndex(
          memoFlattenGamePlans,
          (g) => g.status === Status.IN_PROGRESS
        );
        memoFirstNewIndex = memoFlattenGamePlans.findIndex(
          (g) => g.status === Status.NEW
        );

        memoQuickWinIndex = memoFlattenGamePlans.findIndex(
          (item) => item.type === GamePlanItemType.QUICKWIN
        );
        memoIsGamePlanCompleted = memoFlattenGamePlans
          .slice(0, memoQuickWinIndex - 1)
          .every(
            ({ status, type }) =>
              status === Status.COMPLETED || type === GamePlanItemType.COMPLETED
          );

        const onlyModules = memoGamePlans.filter(
          (gameplan) => gameplan.type === GamePlanItemType.MODULE
        );

        memoIsOneModuleCompleted =
          !onlyModules.length ||
          onlyModules.some((gameplan) => gameplan.status === Status.COMPLETED);
      }

      if (courseProgress?.commitmentStatus !== Status.COMPLETED) {
        setCurrentGamePlanIndex(0);
      } else if (
        currentGamePlanIndex === -1 ||
        currentGamePlanIndex > memoFlattenGamePlans.length - 1
      ) {
        setCurrentGamePlanIndex(
          memoLastInProgressIndex !== -1
            ? memoLastInProgressIndex
            : memoFirstNewIndex
        );
      }

      return {
        memoGamePlans,
        memoFlattenGamePlans,
        memoLastInProgressIndex,
        memoFirstNewIndex,
        memoQuickWinIndex,
        memoIsGamePlanCompleted,
        memoIsOneModuleCompleted,
      };
    }
  }, [
    courseProgress,
    courseModules,
    currentGamePlanIndex,
    setCurrentGamePlanIndex,
    isLoading,
  ]);

  return {
    gamePlans: memo?.memoGamePlans,
    flattenGamePlans: memo?.memoFlattenGamePlans,
    lastInProgressIndex: memo?.memoLastInProgressIndex,
    firstNewIndex: memo?.memoFirstNewIndex,
    quickWinIndex: memo?.memoQuickWinIndex,
    isGamePlanCompleted: memo?.memoIsGamePlanCompleted,
    isOneModuleCompleted: memo?.memoIsOneModuleCompleted,
    isLoading,
    isLearningPathStarted,
    isLearningPathFinished,
  };
};
